<template>
  <button
    v-bind="rootProps"
    :id="id"
    v-tooltip="tooltipOptions"
    :aria-busy="isLoading"
    :aria-describedby="descriptionTextId"
    :aria-disabled="isDisabled || isLoading"
    :aria-label="label"
    :aria-pressed="isPressed"
    :class="activeClasses"
    :loading="isLoading"
    :type="type"
    @click="handleClick"
  >
    <SbIcon
      :name="icon"
      :color="iconColor"
      :is-filled="iconFilled"
      aria-hidden="true"
    />
    <SbLoading
      v-if="isLoading"
      type="spinner"
      size="normal"
      aria-hidden="true"
    />
    <slot name="pin" />
  </button>
</template>

<script setup lang="ts">
import { computed, type ButtonHTMLAttributes } from 'vue'
import SbIcon, { type IconName } from '../Icon'
import SbLoading from '../Loading'
import { Tooltip } from '../../directives'

import type {
  AvailableButtonTypes,
  AvailableButtonVariants,
} from '../Button/lib'
import type { AvailableSizes } from '../../utils'
import type { IconColor } from '../Icon/types'
import { reactiveOmit } from '@vueuse/core'

const vTooltip = Tooltip

export type IconButtonProps = {
  id?: string
  label: string
  variant?: AvailableButtonVariants
  size?: AvailableSizes
  type?: AvailableButtonTypes
  icon?: IconName
  iconFilled?: boolean
  iconColor?: IconColor
  hideTooltip?: boolean
  tooltipPosition?: string
  tooltipVariant?: string
  tooltipZindex?: number
  tooltipOffset?: number[]
  isDisabled?: boolean
  isLoading?: boolean
  isRounded?: boolean
  isPressed?: boolean | undefined
  descriptionTextId?: string
  isClear?: boolean
  noHeight?: boolean
  tooltipWithinTarget?: boolean
} & /* @vue-ignore */ ButtonHTMLAttributes

const props = withDefaults(defineProps<IconButtonProps>(), {
  variant: 'quaternary',
  size: 'normal',
  type: 'submit',
  isDisabled: false,
  isLoading: false,
  iconFilled: false,
  iconColor: undefined,
  isPressed: undefined,
  isClear: true,
  descriptionTextId: undefined,
  tooltipPosition: 'bottom',
  tooltipVariant: 'dark',
  isRounded: false,
  id: undefined,
  icon: undefined,
  tooltipZindex: undefined,
  tooltipWithinTarget: undefined,
})

const rootProps = reactiveOmit(props, [
  'id',
  'label',
  'variant',
  'size',
  'type',
  'icon',
  'iconFilled',
  'iconColor',
  'hideTooltip',
  'tooltipPosition',
  'tooltipVariant',
  'tooltipZindex',
  'tooltipOffset',
  'isDisabled',
  'isLoading',
  'isRounded',
  'isPressed',
  'descriptionTextId',
  'isClear',
  'noHeight',
  'tooltipWithinTarget',
])

defineSlots<{
  pin: (props: {}) => any
}>()

const activeClasses = computed(() => {
  return [
    'sb-icon-button',
    `sb-icon-button--${props.variant}`,
    {
      'sb-icon-button--disabled': props.isDisabled,
      'sb-icon-button--small': props.size === 'small',
      'sb-button--loading': props.isLoading,
      'sb-icon-button--rounded': props.isRounded,
      'sb-icon-button--is-clear': props.isClear,
      'sb-icon-button--no-height': props.noHeight,
    },
  ]
})

const tooltipOptions = computed(() => {
  return {
    label: props.label,
    position: props.tooltipPosition,
    variant: props.tooltipVariant,
    zIndex: props.tooltipZindex,
    show: !props.hideTooltip,
    withinTarget: props.tooltipWithinTarget,
    offset: props.tooltipOffset,
  }
})

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void
}>()

const handleClick = (event: MouseEvent): void => {
  if (!props.isDisabled && !props.isLoading) {
    emit('click', event)
  }
}
</script>
